import { useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import {
  getPlanRegistryVersion,
  setCustomPlanRegistry,
  subscribePlanRegistry,
} from '@/plans';

export function usePlanVariants() {
  const q = useQuery({
    queryKey: ['plan-variants'],
    queryFn: () => api.listPlanVariants(),
    staleTime: 60_000,
  });

  // Hydrate synchronously in render — safe because setCustomPlanRegistry is
  // idempotent and notifies listeners on any actual change. Doing this in a
  // useEffect defers hydration by one paint, which leaves DSPlan/CSPlan
  // showing the previous config right after a save. Guard against no-op
  // updates to avoid infinite re-render loops.
  if (q.data) {
    const cs = q.data.variants
      .filter((v) => v.kind === 'cs')
      .map((v) => ({ key: v.key, name: v.name, basedOn: v.basedOn ?? '', config: v.config }));
    const ds = q.data.variants
      .filter((v) => v.kind === 'ds')
      .map((v) => ({ key: v.key, name: v.name, basedOn: v.basedOn ?? '', config: v.config }));
    const defaults: { ds?: string; cs?: string } = {};
    for (const d of q.data.defaults) defaults[d.kind] = d.variantKey;
    hydrateIfChanged({ ds, cs }, defaults);
  }

  return q;
}

// Cache the last-serialized snapshot to skip redundant writes.
let lastSnapshot = '';
function hydrateIfChanged(
  reg: { ds: unknown[]; cs: unknown[] },
  defaults: { ds?: string; cs?: string },
) {
  const snap = JSON.stringify({ reg, defaults });
  if (snap === lastSnapshot) return;
  lastSnapshot = snap;
  setCustomPlanRegistry(
    reg as Parameters<typeof setCustomPlanRegistry>[0],
    defaults,
  );
}

// Hook to force a re-render of consumers whenever the registry changes.
export function usePlanRegistryVersion(): number {
  return useSyncExternalStore(subscribePlanRegistry, getPlanRegistryVersion);
}
